
import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

interface BasicInfoFormProps {
  nursingHome: NursingHome;
  onSave: (data: Partial<NursingHome>) => Promise<NursingHome>;
  isSaving: boolean;
}

const formSchema = z.object({
  center_name: z.string().min(1, "Nursing home name is required"),
  short_description: z.string().optional(),
  description: z.string().min(1, "Description is required"),
  email: z.string().email("Invalid email address").min(1, "Email is required"),
  standardized_phone_number: z.string().min(1, "Phone number is required"),
  official_website: z.string().url("Invalid URL format").optional().or(z.literal('')),
  street_name: z.string().min(1, "Street address is required"),
  town: z.string().min(1, "Town/City is required"),
  county: z.string().min(1, "County is required"),
  eir_code: z.string().optional(),
  person_in_charge: z.string().optional(),
  manager_name: z.string().optional(),
  year_established: z.number().int().positive().optional().or(z.literal('')).transform(val => 
    val === '' ? undefined : typeof val === 'string' ? parseInt(val) : val
  ),
});

export function BasicInfoForm({ nursingHome, onSave, isSaving }: BasicInfoFormProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      center_name: nursingHome.center_name || '',
      short_description: nursingHome.short_description || '',
      description: nursingHome.description || '',
      email: nursingHome.email || '',
      standardized_phone_number: nursingHome.standardized_phone_number || '',
      official_website: nursingHome.official_website || '',
      street_name: nursingHome.street_name || '',
      town: nursingHome.town || '',
      county: nursingHome.county || '',
      eir_code: nursingHome.eir_code || '',
      person_in_charge: nursingHome.person_in_charge || '',
      manager_name: nursingHome.manager_name || '',
      year_established: nursingHome.year_established || undefined,
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      await onSave(data);
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="center_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Nursing Home Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter nursing home name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="year_established"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Year Established</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="e.g. 1995" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="short_description"
                render={({ field }) => (
                  <FormItem className="col-span-1 md:col-span-2">
                    <FormLabel>Short Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Brief description for listings"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="col-span-1 md:col-span-2">
                    <FormLabel required>Full Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Detailed description of your nursing home"
                        className="min-h-[200px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="border-t pt-4 mt-6">
              <h3 className="font-medium mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Email Address</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="contact@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="standardized_phone_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Phone Number</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. +353 1 123 4567" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="official_website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website</FormLabel>
                      <FormControl>
                        <Input placeholder="https://www.example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="person_in_charge"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Person in Charge</FormLabel>
                      <FormControl>
                        <Input placeholder="Full name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="manager_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Manager Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Full name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="border-t pt-4 mt-6">
              <h3 className="font-medium mb-4">Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="street_name"
                  render={({ field }) => (
                    <FormItem className="col-span-1 md:col-span-2">
                      <FormLabel required>Street Address</FormLabel>
                      <FormControl>
                        <Input placeholder="123 Main Street" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="town"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Town/City</FormLabel>
                      <FormControl>
                        <Input placeholder="Dublin" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="county"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>County</FormLabel>
                      <FormControl>
                        <Input placeholder="Dublin" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="eir_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Eircode</FormLabel>
                      <FormControl>
                        <Input placeholder="A12 BC34" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="border-t bg-muted/50 py-4">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
}
